import React from 'react'
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { TOCWidgetData, TOCEntry, DashboardConfig } from '../../../types/gotypes'
import { DashboardLayout } from '../../../types/gotypes'

/**
 * Extract TOC entries automatically from a dashboard config by scanning for
 * title_hero headings and page names.
 */
export const extractTOCEntries = (config: DashboardConfig | undefined, pageIndex: number): TOCEntry[] => {
  if (!config) return []

  const entries: TOCEntry[] = []
  const pages = config.pages && config.pages.length > 0 ? config.pages : [{ id: 'default', name: 'Page 1', layout: config.layout }]
  const page = pages[pageIndex]
  if (!page) return entries

  const scanLayout = (layout: DashboardLayout, pIdx: number) => {
    layout.rows.forEach((row, rowIndex) => {
      row.cells.forEach((cell) => {
        if (cell.widget_type === 'title_hero' && cell.title_hero_data?.heading) {
          entries.push({
            label: cell.title_hero_data.heading,
            target_id: `row-${pIdx}-${rowIndex}`,
          })
        }
      })
    })
  }

  scanLayout(page.layout, pageIndex)
  return entries
}

interface TOCWidgetRendererProps {
  data: TOCWidgetData
  dashboardConfig?: DashboardConfig
  currentPageIndex?: number
}

export const TOCWidgetRenderer: React.FC<TOCWidgetRendererProps> = ({
  data,
  dashboardConfig,
  currentPageIndex = 0,
}) => {
  const entries = data.mode === 'auto'
    ? extractTOCEntries(dashboardConfig, currentPageIndex)
    : (data.entries || [])

  const handleClick = (targetId: string | undefined) => {
    if (!targetId) return
    const el = document.getElementById(targetId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <Box
      sx={{
        borderLeft: '4px solid',
        borderColor: 'info.main',
        borderRadius: 1,
        backgroundColor: '#fafafa',
        p: 2,
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
        Contents
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {data.mode === 'auto' ? 'Add Title widgets to auto-generate entries' : 'No entries defined'}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {entries.map((entry, i) => (
            <ListItemButton
              key={i}
              onClick={() => handleClick(entry.target_id)}
              sx={{
                borderRadius: 0.5,
                py: 0.25,
                '&:hover': { backgroundColor: 'info.lighter' },
              }}
            >
              <ListItemText
                primary={entry.label}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  )
}

interface TOCWidgetEditorProps {
  data: TOCWidgetData | undefined
  onDataChange: (data: TOCWidgetData) => void
  dashboardConfig?: DashboardConfig
  currentPageIndex?: number
}

export const TOCWidgetEditor: React.FC<TOCWidgetEditorProps> = ({
  data,
  onDataChange,
  dashboardConfig,
  currentPageIndex = 0,
}) => {
  const current: TOCWidgetData = data || { mode: 'auto' }
  const isAuto = current.mode === 'auto'

  const autoEntries = extractTOCEntries(dashboardConfig, currentPageIndex)

  const update = (partial: Partial<TOCWidgetData>) => {
    onDataChange({ ...current, ...partial })
  }

  const handleToggleMode = () => {
    update({ mode: isAuto ? 'manual' : 'auto' })
  }

  const handleAddEntry = () => {
    const entries = [...(current.entries || []), { label: '', target_id: '' }]
    update({ entries })
  }

  const handleRemoveEntry = (index: number) => {
    const entries = (current.entries || []).filter((_, i) => i !== index)
    update({ entries })
  }

  const handleUpdateEntry = (index: number, partial: Partial<TOCEntry>) => {
    const entries = (current.entries || []).map((e, i) =>
      i === index ? { ...e, ...partial } : e
    )
    update({ entries })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={isAuto}
            onChange={handleToggleMode}
            color="info"
          />
        }
        label={isAuto ? 'Auto-generate from Title widgets' : 'Manual entries'}
      />

      {isAuto ? (
        <Box>
          <Typography variant="overline" color="text.secondary">Preview</Typography>
          {autoEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No Title widgets found on this page. Add Title/Hero widgets and they will appear here automatically.
            </Typography>
          ) : (
            <List dense disablePadding>
              {autoEntries.map((entry, i) => (
                <ListItemButton key={i} disabled sx={{ borderRadius: 0.5, py: 0.25 }}>
                  <ListItemText
                    primary={entry.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(current.entries || []).map((entry, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label={`Entry ${i + 1}`}
                size="small"
                fullWidth
                value={entry.label}
                onChange={(e) => handleUpdateEntry(i, { label: e.target.value })}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveEntry(i)}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <IconButton
            size="small"
            onClick={handleAddEntry}
            sx={{ alignSelf: 'flex-start', color: 'info.main' }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}
